// The in-result pick for /tools/spreader-settings: the product the reader is
// dosing, with a bag count when they entered a lawn size. The spreaders
// themselves sit in the ToolPicksBlock below the settings table, so the two
// surfaces never show the same item.

import { FERTILIZER_PRODUCTS } from '@/data/fertilizer-products'
import { WEED_AND_FEED_PRODUCTS } from '@/data/weed-and-feed-products'
import { SPREADER_PRODUCT_ALIASES, type SpreaderProduct } from '@/data/spreader-settings'
import { getProductByKey } from '@/data/guide-product-recommendations'
import { amazonUrl } from '@/lib/affiliate'
import { amazonImageUrl } from '@/lib/amazon-image'
import { buildToolResultPick, parseBagLbs, type ToolResultPickData } from '@/lib/tool-result-pick'

export const SPREADER_SETTINGS_TOOL_SLUG = 'spreader-settings'

/**
 * @param product   the selected SpreaderProduct
 * @param totalLbs  product for the entered area (granular only), else null
 * @param prohibited true when the label forbids this spreader: no pick then,
 *                  since a buy link reads as "go spread it with that".
 */
export function getSpreaderResultPick(opts: {
  product: SpreaderProduct
  totalLbs: number | null
  prohibited: boolean
  now?: Date
}): ToolResultPickData | null {
  const { product, prohibited, now } = opts
  if (prohibited) return null
  const totalLbs = product.form === 'granular' ? opts.totalLbs : null

  if (product.sourceCatalog === 'fertilizer-products') {
    const fp = FERTILIZER_PRODUCTS.find((p) => p.id === product.id)
    const rec = fp?.productKey ? getProductByKey(fp.productKey) : undefined
    if (!fp || !rec) return null
    return buildToolResultPick({
      toolSlug: SPREADER_SETTINGS_TOOL_SLUG,
      product: {
        name: rec.name,
        affiliateUrl: rec.affiliateUrl,
        imageUrl: rec.imageUrl,
        reason: `${fp.npk} · ${fp.bagLabel}`,
      },
      totalLbs,
      // The listing's own weight wins; the label bag is the fallback.
      bagLbs: parseBagLbs(rec.name) ?? fp.bagLbs ?? null,
      now,
    })
  }

  const wf = WEED_AND_FEED_PRODUCTS.find(
    (p) => p.id === product.id || SPREADER_PRODUCT_ALIASES[p.id] === product.id,
  )
  if (!wf?.asin) return null
  const bag = wf.bagOptions?.[0]
  return buildToolResultPick({
    toolSlug: SPREADER_SETTINGS_TOOL_SLUG,
    product: {
      name: wf.name,
      affiliateUrl: amazonUrl(wf.asin),
      asin: wf.asin,
      imageUrl: wf.imageId ? amazonImageUrl(wf.imageId) : null,
      reason: product.coverageLabel,
    },
    totalLbs,
    bagLbs: bag?.weightLbs ?? null,
    now,
  })
}
